import {
  IvaENUM,
  TIPO_IDENTIFICACIONES_CHOICE,
  TIPO_PLANES_CHOICE,
  TIPO_SERVICIOS_CHOICE,
  TipoContribuyenteRucChoice,
  TipoIdentificacionEnumChoice,
  TipoPlanEnumChoice,
  TipoServicioEnumChoice,
  ValorRadioBase,
  fnMostrarTipoTarjeta,
  gridSizeMdLg12,
  gridSizeMdLg6,
  hasPermission,
  texto,
} from "@/shared";
import {
  IPaymentMethodV2,
  TipoPagoV2Enum,
  TIPO_PAGO_V2_OPTIONS,
} from "@/shared/interfaces/erp/system-settings";
import {
  CustomAutocomplete,
  CustomAutocompleteArrString,
  CustomNote,
  CustomNumberTextField,
  CustomTextField,
  CustomTextFieldNoForm,
  CustomTextFieldNumberComponente,
  SampleCheckbox,
  SampleDatePicker,
  TabTextLabel,
} from "@/shared/components/ui";
import { TiposTextFieldNumberEnum } from "@/shared/components/ui/CustomTextFieldNumberComponente";
import {
  Cooperativa,
  EntidadesFinancieras,
  IDataProspecto,
  IFormPreventa,
  IPaymentMethod,
  Plan,
  TipoInstalacion,
} from "@/shared/interfaces";
import { usePlanStore } from "@/store/erp/system-settings/plan.store";
import { useTypeInstalation } from "@/store/erp/system-settings/type-instalation";
import { Edit } from "@mui/icons-material";
import { Alert, Grid, InputAdornment, Typography } from "@mui/material";
import dayjs from "dayjs";
import { UseFormReturn } from "react-hook-form";
import { TooltipIconEdit } from "../SavePreventa";
import { PermissionsEnum } from "@/auth/interfaces";

interface TabServicioPreventaProps {
  dataProspecto: IDataProspecto;
  editSupervisor: boolean;
  form: UseFormReturn<IFormPreventa, any, IFormPreventa>;
  isPendingValorInstalacion?: boolean;
  handleFormaPago?: (e: IPaymentMethod) => Promise<void>;
  dataCooperativa: Cooperativa[] | undefined;
  isLoadingCooperativa: boolean;
  handleValidarTarjeta: (numeroTarjeta: string) => void;
  tipoTarjeta: string | null;
  planDiscTerEdad: boolean;
  handleSelectPlan: (plan: Plan) => Promise<void>;
  isLoadingEntidadFinanciero?: boolean;
  dataEntidadFinanciero?: any;
  handlePlanes: () => Plan[];
  handleValorInstalacion?: () => Promise<void>;
  handleValidarTipoVenta: string;
  // V2 - 3 niveles
  handleTipoPagoV2Change: (tipoPago: TipoPagoV2Enum) => void;
  handleEntidadV2Change: (entidadCodigo: string) => void;
  handleFormaPagoV2Change: (formaPago: IPaymentMethodV2) => void;
  entidadesV2Filtradas: EntidadesFinancieras[];
  isLoadingEntidadesV2: boolean;
  formasPagoV2Filtradas: IPaymentMethodV2[];
  isLoadingFormasPagoV2: boolean;
}
export default function TabServicioPreventa({
  dataProspecto,
  editSupervisor,
  form,
  // handleFormaPago, // No usado en V2
  // isPendingValorInstalacion,
  dataCooperativa,
  isLoadingCooperativa,
  handleValidarTarjeta,
  tipoTarjeta,
  planDiscTerEdad,
  handleSelectPlan,
  // isLoadingEntidadFinanciero, // No usado en V2
  // dataEntidadFinanciero, // No usado en V2
  handlePlanes,
  handleValidarTipoVenta,
  // V2 - 3 niveles
  handleTipoPagoV2Change,
  handleEntidadV2Change,
  handleFormaPagoV2Change,
  entidadesV2Filtradas,
  isLoadingEntidadesV2,
  formasPagoV2Filtradas,
  isLoadingFormasPagoV2,
}: TabServicioPreventaProps) {
  const {
    control,
    reset,
    watch,
    formState: { errors },
  } = form;

  // Watch para hacer reactivos los campos condicionales
  const tipoPagoV2 = watch('tipo_pago_v2');
  const entidadFinanciera = watch('entidad_financiera');
  const formaPagoV2 = watch('forma_pago_v2');
  const sector = watch('sector');
  const tipoPlan = watch('tipo_plan');
  const tipoServicio = watch('tipo_servicio');
  const plan = watch('plan');
  const promocion = watch('promocion');

  const typInstalation = useTypeInstalation((s) => s.typInstalation);
  const isLoadingTypeInstalation = useTypeInstalation(
    (s) => s.isLoadingTypeInstalation
  );

  const isLoadingPlans = usePlanStore((s) => s.isLoadingPlans);

  return (
    <Grid container spacing={3}>
      <Grid item {...gridSizeMdLg12} display={"flex"}>
        <TabTextLabel text={texto.pagos} mt={0} pb={2} pl={0} pt={0} />
        {editSupervisor && <TooltipIconEdit />}
      </Grid>

      {/* ============ NIVEL 1: Tipo de Pago (DÉBITO/CRÉDITO) ============ */}
      <CustomAutocompleteArrString
        size={gridSizeMdLg6}
        label="Tipo de Pago *"
        name="tipo_pago_v2"
        options={TIPO_PAGO_V2_OPTIONS}
        defaultValue={form.getValues().tipo_pago_v2 || ""}
        isLoadingData={false}
        control={control}
        error={errors.tipo_pago_v2}
        helperText={errors.tipo_pago_v2?.message}
        onChangeValue={(e) => handleTipoPagoV2Change(e as TipoPagoV2Enum)}
      />

      {/* ============ NIVEL 2: Entidad Bancaria (filtrada por tipo de pago) - No aplica para RECAUDACIÓN ============ */}
      {tipoPagoV2 && tipoPagoV2 !== TipoPagoV2Enum.RECAUDACION && (
        <CustomAutocomplete<EntidadesFinancieras>
          key={`entidad-${tipoPagoV2}`}
          textFieldKey={`entidad-field-${tipoPagoV2}`} // Forzar re-render del Controller interno
          size={gridSizeMdLg6}
          label="Entidad Bancaria *"
          name="entidad_financiera"
          options={entidadesV2Filtradas || []}
          valueKey="nombre"
          actualValueKey="codigo"
          defaultValue={form.getValues().entidad_financiera || ""}
          optionLabelForEdit={form.getValues().entidad_financiera || ""}
          isLoadingData={isLoadingEntidadesV2}
          control={control}
          error={errors.entidad_financiera}
          helperText={errors.entidad_financiera?.message}
          onChangeValue={(e) => handleEntidadV2Change(e)}
        />
      )}

      {/* ============ NIVEL 3: Forma de Pago V2 (filtrada por entidad + tipo) - No aplica para RECAUDACIÓN ============ */}
      {tipoPagoV2 && tipoPagoV2 !== TipoPagoV2Enum.RECAUDACION && entidadFinanciera && (
        <CustomAutocomplete<IPaymentMethodV2>
          key={`forma-pago-${tipoPagoV2}-${entidadFinanciera}`}
          textFieldKey={`forma-pago-field-${tipoPagoV2}-${entidadFinanciera}`} // Forzar re-render del Controller interno
          size={gridSizeMdLg6}
          label="Forma de Pago *"
          name="forma_pago_v2"
          options={formasPagoV2Filtradas || []}
          valueKey="nombre"
          actualValueKey="codigo"
          defaultValue={form.getValues().forma_pago_v2 || ""}
          optionLabelForEdit={formasPagoV2Filtradas?.find(fp => fp.codigo === form.getValues().forma_pago_v2)?.nombre || ""}
          isLoadingData={isLoadingFormasPagoV2}
          control={control}
          error={errors.forma_pago_v2}
          helperText={errors.forma_pago_v2?.message}
          onChangeRawValue={(e) => handleFormaPagoV2Change(e)}
        />
      )}

      {/* ============ Campos condicionales según tipo de pago V2 ============ */}
      {tipoPagoV2 === TipoPagoV2Enum.DEBITO && formaPagoV2 && (
        <>
          {/* Campo "Tipo de Cuenta" oculto - se setea automáticamente desde handleFormaPagoV2Change
              basándose en el nombre de "Forma de Pago":
              - "Cuenta Amiga" → "AMIGA"
              - "Cuenta Corriente" → "CORRIENTE"
              - "Cuenta Ahorros" → "AHORRO"
          */}

          {!isLoadingCooperativa && dataCooperativa?.length != 0 && (
            <CustomAutocomplete<Cooperativa>
              size={gridSizeMdLg6}
              label={texto.cooperativa}
              name="cooperativa"
              // options
              options={dataCooperativa || []}
              valueKey="nombre" // name is the fk
              actualValueKey="codigo"
              defaultValue={form.getValues().cooperativa || ""}
              isLoadingData={isLoadingCooperativa}
              // validation
              control={control}
              error={errors.cooperativa}
              helperText={errors.cooperativa?.message}
            /* onChangeValue={(e) => {
                reset({ ...form.getValues(), cooperativa: e });
              }} */
            />
          )}

          <CustomTextFieldNumberComponente
            type={TiposTextFieldNumberEnum.TARJETA}
            size={gridSizeMdLg12}
            label={texto.numero_cuenta}
            error={errors.numero_cuenta}
            helperText={errors.numero_cuenta?.message}
            control={form.control}
            name="numero_cuenta"
            defaultValue={form.getValues().numero_cuenta}
            max={25}
          />
        </>
      )}

      {/* ============ Campos para CRÉDITO (tarjeta) ============ */}
      {tipoPagoV2 === TipoPagoV2Enum.CREDITO && formaPagoV2 && (
        <>
          <SampleDatePicker
            format="YYYY-MM"
            views={["year", "month"]}
            label="Fecha Caducidad *"
            name="tarjeta_fecha_caducidad"
            minDate={dayjs(new Date())}
            control={form.control}
            defaultValue={form.getValues().tarjeta_fecha_caducidad ?? ""}
            error={errors.tarjeta_fecha_caducidad}
            helperText={errors.tarjeta_fecha_caducidad?.message}
            size={gridSizeMdLg6}
          />

          <CustomTextFieldNumberComponente
            type={TiposTextFieldNumberEnum.TARJETA}
            startAdornment={
              fnMostrarTipoTarjeta(tipoTarjeta) ? (
                <img
                  src={fnMostrarTipoTarjeta(tipoTarjeta)}
                  alt="imge-tarjeta"
                  width={"50px"}
                />
              ) : null
            }
            size={gridSizeMdLg6}
            label={texto.ingresar_num_tarjeta}
            error={errors.numero_tarjeta}
            helperText={errors.numero_tarjeta?.message}
            control={form.control}
            name="numero_tarjeta"
            defaultValue={form.getValues().numero_tarjeta ?? ""}
            max={24}
            onChangeValue={(e) => handleValidarTarjeta(e)}
          />
        </>
      )}

      {dataProspecto.tipo_identificacion == TipoIdentificacionEnumChoice.RUC &&
        dataProspecto.tipo_contribuyente == TipoContribuyenteRucChoice.PERSONA_NATURAL &&
        tipoPagoV2 && formaPagoV2 &&
        (
          <>

            <Grid item {...gridSizeMdLg12} display={"flex"}>
              <TabTextLabel text={'Autorización'} mt={0} pb={2} pl={0} pt={0} />
              {editSupervisor && <TooltipIconEdit />}
            </Grid>

            <CustomAutocompleteArrString
              size={gridSizeMdLg6}
              label={'Seleccionar'}
              name="tipo_autorizacion_buro"
              options={TIPO_IDENTIFICACIONES_CHOICE || []}
              defaultValue={form.getValues().tipo_autorizacion_buro || TipoIdentificacionEnumChoice.CEDULA}
              isLoadingData={false}
              control={control}
              error={errors.tipo_autorizacion_buro}
              helperText={errors.tipo_autorizacion_buro?.message}
            />

            <CustomTextFieldNoForm
              disabled
              size={gridSizeMdLg6}
              label="Identificación"
              value={
                form.getValues().tipo_autorizacion_buro == TipoIdentificacionEnumChoice.RUC ?
                  dataProspecto.identificacion :
                  dataProspecto.identificacion.substring(0, 10)
              }
            />

            <CustomNote
              note={`Por favor, selecciona la forma de autorización para el buró crediticio.\nSi la forma de pago es por débito, asegúrate de que la información coincida con la del documento de la cuenta bancaria.`}
            />

          </>
        )
      }



      <Grid item {...gridSizeMdLg12} display={"flex"}>
        <TabTextLabel text={texto.instalacion} mt={0} pb={0} pl={0} pt={2} />
        {editSupervisor && <TooltipIconEdit />}
      </Grid>

      <CustomAutocomplete<TipoInstalacion>
        size={gridSizeMdLg6}
        label={texto.tipo_instalacion}
        name="tipo_instalacion"
        // options
        options={typInstalation || []}
        valueKey="nombre" // name is the fk
        actualValueKey="codigo"
        defaultValue={form.getValues().tipo_instalacion || ""}
        isLoadingData={isLoadingTypeInstalation}
        // validation
        control={control}
        error={errors.tipo_instalacion}
        helperText={errors.tipo_instalacion?.message}
        onChangeValue={(e) => {
          form.setValue("tipo_instalacion", e);
        }}
      />

      <Grid item xs={12} display={"flex"}>
        <TabTextLabel text={texto.plan} mt={0} pb={2} pl={0} pt={0} />
        {editSupervisor && <TooltipIconEdit />}
      </Grid>

      {/* {dataProspecto.es_ayuda_social ||
        form.getValues().es_discapacitado ||
        form.getValues().es_tercera_edad ? null : (
        <Grid item xs={12} display={"flex"}>
          <SampleCheckbox
            size={gridSizeMdLg4}
            label="Aplica Promo Gremios"
            control={form.control}
            name="aplica_gremios"
            defaultValue={!!form.getValues().aplica_gremios!}
            onChangeValue={(e) => {
              form.reset({ ...form.getValues(), aplica_gremios: e, plan: "" });
            }}
          />
        </Grid>
      )} */}

      {sector && (formaPagoV2 || tipoPagoV2 === TipoPagoV2Enum.RECAUDACION) ? (
        <>

          <CustomAutocompleteArrString
            disabled={planDiscTerEdad}
            size={gridSizeMdLg6}
            label={texto.tipo_plan}
            name="tipo_plan"
            options={TIPO_PLANES_CHOICE.filter(
              (e) =>
                e == TipoPlanEnumChoice.HOGAR || e == TipoPlanEnumChoice.PYMES
            )}
            defaultValue={form.getValues().tipo_plan || ""}
            isLoadingData={false}
            control={control}
            error={errors.tipo_plan}
            helperText={errors.tipo_plan?.message}
            onChangeValue={(e) => {
              reset({
                ...form.getValues(),
                es_router_ac: false,
                tipo_plan: e,
                tipo_servicio: "",
                plan: "",
                promocion: "",
                velocidad: "",
                valor_plan: "",
              });
            }}
          />

          {tipoPlan && (
            <CustomAutocompleteArrString
              disabled={planDiscTerEdad}
              size={gridSizeMdLg6}
              label={texto.tipo_servicio}
              name="tipo_servicio"
              options={TIPO_SERVICIOS_CHOICE || []}
              defaultValue={form.getValues().tipo_servicio || ""}
              isLoadingData={false}
              control={control}
              error={errors.tipo_servicio}
              helperText={errors.tipo_servicio?.message}
              onChangeValue={(e) => {
                reset({
                  ...form.getValues(),
                  es_router_ac: e == TipoServicioEnumChoice.RADIO,
                  tipo_servicio: e,
                  plan: "",
                  promocion: "",
                  velocidad: "",
                  valor_plan: "",
                  numero_cuotas_router_ac: 0,
                  numero_cantidad_router_ac: 0,
                });

                if (e == TipoServicioEnumChoice.RADIO) {
                  form.setValue("numero_cuotas_router_ac", 1);
                  form.setValue("numero_cantidad_router_ac", 1);
                }
              }}
            />
          )}

          {tipoServicio && (
            <CustomAutocomplete<Plan>
              size={gridSizeMdLg6}
              disabled={planDiscTerEdad}
              label={texto.plan}
              name="plan"
              options={handlePlanes()}
              /* options={
                form.getValues()?.aplica_gremios
                  ? handlePlanes().filter(
                    (x) => x.nombre == "PLAN FAMILY MAX - 500MBPS"
                  )
                  : handlePlanes()
              } */
              valueKey="nombre"
              actualValueKey="codigo"
              defaultValue={form.getValues().plan || ""}
              isLoadingData={isLoadingPlans}
              control={control}
              error={errors.plan}
              helperText={errors.plan?.message}
              onChangeRawValue={async (e) => handleSelectPlan(e)}
            />
          )}

          {plan && (
            <CustomTextField
              disabled={true}
              control={control}
              name="velocidad"
              defaultValue={form.getValues().velocidad || ""}
              size={gridSizeMdLg6}
              label={texto.velocidad_internet}
              error={errors.velocidad}
              helperText={errors.velocidad?.message}
              endAdornmentInput={
                <InputAdornment position="end">
                  {form.getValues().unidad}
                </InputAdornment>
              }
            />
          )}

          {plan && (
            <CustomTextField
              disabled={true}
              control={control}
              name="valor_plan"
              defaultValue={form.getValues().valor_plan || ""}
              size={gridSizeMdLg6}
              label={texto.valor_plan}
              error={errors.valor_plan}
              helperText={errors.valor_plan?.message}
            />
          )}

          {dataProspecto.es_ayuda_social &&
            !(
              form.getValues().es_discapacitado ||
              form.getValues().es_tercera_edad
            ) && (
              <Grid item {...gridSizeMdLg12}>
                <Alert variant="outlined" severity="info">
                  {texto.plan_ayuda_social}
                </Alert>
              </Grid>
            )}

          {(form.getValues().es_discapacitado ||
            form.getValues().es_tercera_edad) && (
              <Grid item {...gridSizeMdLg12}>
                <Alert variant="outlined" severity="info">
                  {texto.plan_3edad_disc}
                </Alert>
              </Grid>
            )}

          {dataProspecto.es_gremio && (
            <Grid item {...gridSizeMdLg12}>
              <Alert variant="outlined" severity="info">
                {texto.plan_gremio}
              </Alert>
            </Grid>
          )

          }
          {dataProspecto.es_curador_tutor && (
            <Grid item {...gridSizeMdLg12}>
              <Alert variant="outlined" severity="info">
                {texto.plan_curador_tutor}
              </Alert>
            </Grid>
          )}

          {dataProspecto.es_bono_humano && (
            <Grid item {...gridSizeMdLg12}>
              <Alert variant="outlined" severity="info">
                {texto.plan_bono_desarrollo_humano}
              </Alert>
            </Grid>
          )}

          {tipoServicio === TipoServicioEnumChoice.RADIO && (
            <Grid item {...gridSizeMdLg12}>
              <Alert variant="outlined" severity="info">
                {`Nota: Al seleccionar el tipo de servicio por RADIO el valor de instalación es de $${ValorRadioBase} valor incluye IVA.`}
              </Alert>
            </Grid>
          )}

          {promocion && (
            <Grid item {...gridSizeMdLg12}>
              <Alert variant="outlined" severity="success">
                {`Plan aplica promoción: ${form.getValues().promocion_nombre}.`}
              </Alert>
            </Grid>
          )}

          {plan && handleValidarTipoVenta != 'HOGAR' && (
            <>
              <Grid item xs={12}>
                <TabTextLabel
                  text={texto.valor_instalacion}
                  mt={0}
                  pb={2}
                  pl={0}
                  pt={0}
                />
              </Grid>

              <CustomTextFieldNoForm
                size={gridSizeMdLg6}
                label={texto.valor_instalacion_sin_iva}
                value={0}
                defaultValue={0}
              />

              <CustomTextFieldNoForm
                size={gridSizeMdLg6}
                label={texto.valor_total_instalacion}
                value={0}
                defaultValue={0}
              />
            </>
          )}

          {plan && handleValidarTipoVenta == 'HOGAR' && (
            <>
              <Grid item xs={12}>
                <TabTextLabel
                  text={texto.valor_instalacion}
                  mt={0}
                  pb={2}
                  pl={0}
                  pt={0}
                />
              </Grid>

                <CustomNumberTextField
                  endAdornment={!hasPermission(PermissionsEnum.liberacion_valor_instalacion) 
                    ? <Edit color="action" /> 
                    : null}
                  disabled={!hasPermission(PermissionsEnum.liberacion_valor_instalacion)}
                  name="valor_instalacion" // valor sin iva
                  label={texto.valor_instalacion_sin_iva}
                  control={form.control}
                  defaultValue={form.getValues().valor_instalacion || ""}
                  error={errors.valor_instalacion}
                  helperText={errors.valor_instalacion?.message}
                  size={gridSizeMdLg6}
                  step={0.01}
                  onChangeValue={(value) => {
                    if (!value) return;
                    const total = +value + +value * IvaENUM.IVA;
                    form.setValue("valor_instalacion_total", total.toFixed(2));
                  }}
                />

              <CustomTextField
                endAdornmentInput={
                  editSupervisor ? <Edit color="action" /> : null
                }
                disabled={true}
                name="valor_instalacion_total"
                label={texto.valor_total_instalacion}
                control={form.control}
                defaultValue={form.getValues().valor_instalacion_total || ""}
                error={errors.valor_instalacion_total}
                helperText={errors.valor_instalacion_total?.message}
                size={gridSizeMdLg6}
              />

              {+form.getValues().valor_instalacion_total > 0 &&
                !editSupervisor && (
                  <SampleCheckbox
                    sizeCheck="medium"
                    label="Este servicio incluye un costo de instalación. Si lo deseas, puedes solicitar a tu supervisor que lo elimine."
                    name="no_cobrar"
                    control={form.control}
                    defaultValue={form.getValues().no_cobrar || false}
                    size={gridSizeMdLg12}
                  />
                )}
            </>
          )}
        </>
      ) : (
        <Grid item xs={12}>
          <Typography
            variant="subtitle2"
            style={{
              color: "#6C737F",
            }}
          >
            {texto.sin_plan_residencia}
          </Typography>
        </Grid>
      )}
    </Grid>
  );
}
